import { FC, useMemo, useCallback, ChangeEvent, useEffect, useState, useRef } from 'react'
import cn from 'classnames'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'
import Button from 'components/features/pfm/Button/Button'
import styles from 'components/features/pfm/ProviderSearchPane/ProviderSearchPane.styl'
import headerStyles from 'components/features/pfm/FloatingPaneHeader/FloatingPaneHeader.styl'
import { ProviderSearchPaneDecoration } from 'components/features/pfm/ProviderSearchPaneDecoration/ProviderSearchPaneDecoration'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import {
  searchProvidersActionCreator,
  startLinkageSessionActionCreator,
  killLinkageSessionActionCreator,
  clearProvidersSearchActionCreator,
} from 'actions/linkageActions'
import { createCompositeId } from 'helpers/linkageHelpers'
import { killAllFloatingPanesActionCreator, ModifyFloatingPaneAction } from 'actions/floatingPanesActions'
import { linkageSessionSelector, foundProvidersSelector, providersSearchQuerySelector } from 'selectors/linkageSelectors'
import PopularProviders from 'components/molecules/PopularProviders'
import { ResultItem } from 'components/features/pfm/ProviderSearchPane/ResultItem'
import { Provider } from 'interfaces/linkage'
import SearchSvg from 'icons/search.svg'
import SvgIcon from 'components/atoms/SvgIcon'
import { isMobileSelector } from 'selectors/viewportSelectors'

type ProviderSearchPaneProps = {}
export const ProviderSearchPane: FC<ProviderSearchPaneProps> = () => {

  const inputRef = useRef<HTMLInputElement | null | undefined>()
  const handleRef = useCallback<(ref: HTMLInputElement | null) => void>((ref) => {
    inputRef.current = ref
  }, [])
  const handleForceInputFocus = useCallback(() => {
    const { current } = inputRef
    if (!current) {
      return
    }

    current.focus()
  }, [])

  const isMobile = useStoreSelector(isMobileSelector)

  const dispatch = useStoreDispatch()
  useEffect(() => {
    !isMobile && handleForceInputFocus()
    dispatch(clearProvidersSearchActionCreator({}))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const foundProviders = useStoreSelector(foundProvidersSelector)

  const handleSelectProvider = useCallback<(provider: Provider) => void>((provider) => {
    if (provider.fast_link_forced) {
      return dispatch(new ModifyFloatingPaneAction({
        type: 'fastlink',
        mode: 'add',
        provider_id: provider.id,
      }))
    }
    dispatch(startLinkageSessionActionCreator.request(provider))
    return
  }, [dispatch])

  const [currentQuery, setCurrentQuery] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [highlightedProviderIndex, setHighlightedProviderIndex] = useState(0)

  const handleOnFocus = useCallback(() => {
    setIsSearchFocused(true)
  }, [setIsSearchFocused])

  const handleOnBlur = useCallback(() => {
    setIsSearchFocused(false)
  }, [setIsSearchFocused])

  const handleClean = useCallback(() => {
    setCurrentQuery('')
    dispatch(clearProvidersSearchActionCreator({}))
  }, [dispatch, setCurrentQuery])

  const handleClearQuery = useCallback(() => {
    setCurrentQuery('')
    handleForceInputFocus()
  }, [handleForceInputFocus])

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      const highlightedProvider = !!foundProviders ? foundProviders[highlightedProviderIndex] : null
      if (highlightedProvider) {
        handleSelectProvider(highlightedProvider)
      }
    } else if (['ArrowDown', 'ArrowRight'].includes(event.key)) {
      setHighlightedProviderIndex(highlightedProviderIndex + 1)
    } else if (['ArrowUp', 'ArrowLeft'].includes(event.key) && highlightedProviderIndex > 0) {
      setHighlightedProviderIndex(highlightedProviderIndex - 1)
    } else if (event.key === 'Tab') {
      !!foundProviders && event.preventDefault()
    } else if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) {
      return
    } else {
      setHighlightedProviderIndex(0)
    }
  }, [highlightedProviderIndex, foundProviders, handleSelectProvider])

  const handleChange = useCallback<(event: ChangeEvent<HTMLInputElement>) => void>((event) => {
    const { value } = event.target
    if (!value) {
      handleClean()
    } else {
      setCurrentQuery(value)
      dispatch(searchProvidersActionCreator.request({ query: value }))
    }
  }, [dispatch, handleClean, setCurrentQuery])

  const session = useStoreSelector(linkageSessionSelector)
  const searchBar = useMemo(() => {
    const inButtonMode = !isSearchFocused && !currentQuery
    const classNames = cn(styles.searchWrapper, { [styles.searchFocused]: !inButtonMode })
    return (
      <fieldset className={ classNames }>
        <ProviderSearchPaneDecoration
          decorationProps={{
            label: inButtonMode ? 'Search' : 'Your Financial Institution',
            mode: 'floatingPane',
          }}
          inputProps={{
            disabled: session !== null,
            autoComplete: 'off',
            onKeyDown: handleKeyDown,
            onChange: handleChange,
            onFocus: handleOnFocus,
            onBlur: handleOnBlur,
            value: currentQuery,
            ...{
              'data-qa-type': 'search',
              'data-qa-marker': 'provider-search',
            } as any,
          }}
          onClean={ handleClean }
          icon={ <i className={ styles.searchIcon }><SvgIcon src={ SearchSvg } /></i> }
          inputRef={ handleRef }
        />
      </fieldset>
    )
  }, [session, handleClean, handleChange, handleOnFocus, handleKeyDown, handleOnBlur, currentQuery, isSearchFocused, handleRef])


  const headerChild = useMemo(() => (
    <>
      <h3 className={ cn(headerStyles.title, styles.header) }>Link New Account</h3>
      { searchBar }
    </>
  ), [searchBar])


  const handleClosePane = useCallback(() => {
    dispatch(killAllFloatingPanesActionCreator())
    if (session) {
      dispatch(killLinkageSessionActionCreator(session.sessionId))
    }
  }, [dispatch, session])

  const handleManualCreation = useCallback(() => {
    dispatch(
      startLinkageSessionActionCreator.request({
        cid: createCompositeId('manual-balance-account', null),
      }),
    )
  }, [dispatch])

  const footerChild = useMemo(() => (
    <CardBlock forceRoundedCorners shadowTheme='medium' className={ styles.addManualAccountButton }>
      <Button fullWidth size='medium' theme='white' onClick={ handleManualCreation }>
        <div className={ styles.addManualAccountButtonContent }>
          <span className={ styles.mainCta }>Add an account manually</span>
        </div>
      </Button>
    </CardBlock>
  ), [handleManualCreation])

  const searchQuery = useStoreSelector(providersSearchQuerySelector)

  const resultsChild = useMemo(() => {

    const addManualAccountButton = (
      <Button
        text='Add an account manually'
        style='hollow'
        fullWidth
        onClick={ handleManualCreation }
      />
    )

    if (!foundProviders) {
      return null
    }

    if (foundProviders.length === 0) {
      return (
        <section className={ styles.dropdownNoResults }>
          No results found for
          <strong> { searchQuery }</strong>
          <br />
          <Button
            text='Try another name'
            style='textOnly'
            onClick={ handleClearQuery }
          />
          <br />
          or
          <br /><br />
          { addManualAccountButton }
        </section>
      )
    }

    return (
      <>
        { foundProviders.map((provider, index) => (
          <ResultItem
            key={ provider.cid }
            query={ searchQuery }
            provider={ provider }
            onClick={ () => handleSelectProvider(provider) }
            isHighlighted={ highlightedProviderIndex === index }
          />
        ))}
      </>
    )
  }, [handleManualCreation, foundProviders, searchQuery, handleClearQuery, highlightedProviderIndex, handleSelectProvider])

  return (
    <FloatingPaneLayout
      type='provider-search'
      header={ headerChild }
      bodyClassName={ styles.body }
      onClose={ handleClosePane }
    >
      { resultsChild }
      { !resultsChild && (
        <div className={ styles.popularProvidersWrapper }>
          <p className={ styles.subtitle }>
            Select from these well-known companies
          </p>
          <PopularProviders />
          <br />
          <p className={ styles.subtitle }>
            or
          </p>
          { footerChild }
        </div>
      )}
    </FloatingPaneLayout>
  )
}
